import { Layers, Pencil, Plus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/**
 * Left sidebar — pick a category to filter services (Salon Desk Services page).
 *
 * @param {{
 *   categories: import('@/types/api').ServiceCategory[],
 *   selectedCategoryId: string,
 *   onSelectCategory: (categoryId: string) => void,
 *   onAddCategory: () => void,
 *   onEditCategory: (category: import('@/types/api').ServiceCategory) => void,
 * }} props
 */
export function ServiceCategoriesSidebar({
  categories,
  selectedCategoryId,
  onSelectCategory,
  onAddCategory,
  onEditCategory,
}) {
  return (
    <aside className="card-surface flex h-full min-h-[320px] flex-col p-3">
      <div className="mb-3 flex items-center justify-between gap-2 border-b border-border/50 pb-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Layers className="h-4 w-4 text-primary" aria-hidden />
          Categories
        </div>
        <Button type="button" size="sm" variant="outline" onClick={onAddCategory}>
          <Plus className="h-3.5 w-3.5" />
          Add
        </Button>
      </div>

      {categories.length === 0 ? (
        <p className="px-1 text-sm text-muted-foreground">
          Add a category first, then add services inside it.
        </p>
      ) : (
        <ul className="space-y-1">
          {categories.map((category) => {
            const isActive = String(category.id) === selectedCategoryId

            return (
              <li key={category.id}>
                <div
                  className={cn(
                    'flex items-center gap-1 rounded-xl transition-colors',
                    isActive ? 'bg-primary/10 ring-1 ring-primary/20' : 'hover:bg-muted/60',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => onSelectCategory(String(category.id))}
                    className={cn(
                      'min-w-0 flex-1 px-3 py-2 text-left text-sm font-medium',
                      isActive ? 'text-primary' : 'text-foreground',
                    )}
                  >
                    <span className="block truncate">{category.name}</span>
                    <span className="text-[11px] font-normal text-muted-foreground">
                      {category.services_count ?? 0} service
                      {(category.services_count ?? 0) === 1 ? '' : 's'}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => onEditCategory(category)}
                    className="mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-background hover:text-foreground"
                    aria-label={`Edit ${category.name}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </aside>
  )
}
